import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    GO_NO_GO_FEEDBACK_MS,
    createGoNoGoStimuli,
    GameShell,
    usePausableTimeout,
} from '../shared';
import { postToNativeBridge } from '../../../lib/ufovRuntime';
import PlayingCard from './go-no-go/PlayingCard';
import { formatGameCopy } from './gameCopy';

const CARD_BACKS = [3, 2, 1];

function vibrateForMissedTarget() {
    const sentToNative = postToNativeBridge('timer-vibrate', {
        durationMs: 120,
        source: 'go-no-go-missed-target',
    });

    if (sentToNative) {
        return;
    }

    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
        return;
    }

    navigator.vibrate([90, 45, 120]);
}

function GoNoGoGame({ copy = {}, initialLevel = 4, isPaused = false, ...props }) {
    const level = Math.max(1, Math.min(8, Math.round(Number(initialLevel) || 4)));
    const responseMs = 1400 - level * 100;
    const { clear: clearResponseTimer, schedule: scheduleResponseTimer } = usePausableTimeout(isPaused);
    const { clear: clearFeedbackTimer, schedule: scheduleFeedbackTimer } = usePausableTimeout(isPaused);
    const respondedRef = useRef(false);
    const [stimuli, setStimuli] = useState(() => createGoNoGoStimuli());
    const [index, setIndex] = useState(0);
    const [batch, setBatch] = useState(0);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [mistakes, setMistakes] = useState(0);
    const [feedback, setFeedback] = useState('');
    const current = stimuli[index];
    const done = index >= stimuli.length;

    const queueNextCard = useCallback(() => {
        clearResponseTimer();
        clearFeedbackTimer();
        scheduleFeedbackTimer(() => {
            setFeedback('');
            setIndex((value) => value + 1);
        }, GO_NO_GO_FEEDBACK_MS);
    }, [clearFeedbackTimer, clearResponseTimer, scheduleFeedbackTimer]);

    const recordCorrect = () => {
        setScore((value) => value + 1);
        setStreak((value) => {
            const nextValue = value + 1;
            setBestStreak((best) => Math.max(best, nextValue));
            return nextValue;
        });
    };

    const recordMistake = () => {
        setMistakes((value) => value + 1);
        setStreak(0);
    };

    useEffect(() => {
        if (!done) {
            return;
        }

        setStimuli(createGoNoGoStimuli());
        setIndex(0);
        setBatch((value) => value + 1);
    }, [done]);

    useEffect(() => {
        clearResponseTimer();
        clearFeedbackTimer();

        if (!current || done) {
            return undefined;
        }

        respondedRef.current = false;
        setFeedback('');
        scheduleResponseTimer(() => {
            if (respondedRef.current) {
                return;
            }

            respondedRef.current = true;
            if (current.go) {
                recordMistake();
                setFeedback('missed-target');
                vibrateForMissedTarget();
            } else {
                recordCorrect();
                setFeedback('good');
            }
            queueNextCard();
        }, responseMs);

        return () => {
            clearResponseTimer();
            clearFeedbackTimer();
        };
    }, [clearFeedbackTimer, clearResponseTimer, current, done, queueNextCard, responseMs, scheduleResponseTimer]);

    useEffect(() => () => {
        clearResponseTimer();
        clearFeedbackTimer();
    }, []);

    const press = () => {
        if (isPaused || !current || done || feedback || respondedRef.current) {
            return;
        }

        respondedRef.current = true;
        clearResponseTimer();

        if (current.go) {
            recordCorrect();
            setFeedback('hit');
        } else {
            recordMistake();
            setFeedback('false-press');
        }

        queueNextCard();
    };

    const rankCopyKey = {
        A: 'rankAce',
        J: 'rankJack',
        K: 'rankKing',
        Q: 'rankQueen',
    };
    const rankLabel = current ? (copy[rankCopyKey[current.rank]] || current.rank) : '';
    const playingCardLabel = formatGameCopy(copy.playingCard, { rank: rankLabel });
    const cardLabel = current ? playingCardLabel : copy.dealingNextCard;

    return (
        <GameShell {...props} copy={copy} disabled={isPaused} metrics={{ level, mistakes, score, streak: bestStreak }} tone="ready">
            <div className="UFOV-brain-games__go-deck-game">
                <p className="UFOV-brain-games__go-rule">
                    {copy.goNoGoRule}
                </p>
                <div className="UFOV-brain-games__go-deck" aria-live="polite">
                    {CARD_BACKS.map((layer) => (
                        <span
                            aria-hidden="true"
                            className={`UFOV-brain-games__go-card-back is-layer-${layer}`}
                            key={layer}
                        >
                            <PlayingCard isBack />
                        </span>
                    ))}
                    {current ? (
                        <div
                            aria-label={cardLabel}
                            className={`UFOV-brain-games__go-active-card ${feedback ? `is-${feedback}` : ''}`.trim()}
                            key={`${batch}-${current.id}`}
                            role="img"
                        >
                            <PlayingCard card={current} />
                        </div>
                    ) : null}
                </div>
                <button
                    aria-label={copy.goNoGoRule}
                    className="UFOV-brain-games__go-press"
                    disabled={isPaused || !current || done}
                    onClick={press}
                    type="button"
                >
                    {copy.pressButton}
                </button>
            </div>
        </GameShell>
    );
}

export default GoNoGoGame;
